import { useEffect } from 'react';

import { parseFontVariant } from '@air/utils-fonts';

/**
 * Default marker attribute for the loader's injected `<head>` tag so tests (and debugging humans)
 * can find it. Callers with an established attribute of their own (e.g. the public headline font
 * loader) can override it per hook call.
 */
export const FONT_LOADER_ATTRIBUTE = 'data-font-loader';

export const GOOGLE_FONTS_CSS2_URL = 'https://fonts.googleapis.com/css2';
// The css2 default is 400 only; also request 700 so bold elements render a real bold instead of a
// synthesized one.
const DEFAULT_GOOGLE_FONT_WEIGHTS = '400;700';

/** A font the loader can register: a built-in (Google Fonts) family, or an uploaded font file. */
export type FontLoaderFont = {
  fontFamily: string;
  /** Backend font variant (for example `regular`, `700`, `italic`, or `700italic`). */
  variant?: string;
  /**
   * Uploaded font file the document must register via `@font-face` — uploaded fonts may not exist
   * on Google Fonts at all. When set, the Google Fonts stylesheet is skipped entirely.
   */
  fontFaceUrl?: string;
};

// Escape a font family for embedding in a single-quoted CSS string ('Family' in font-family /
// @font-face). Backslashes must be escaped BEFORE quotes: a trailing `\` would otherwise escape
// the closing quote and unterminate the CSS string.
export const escapeCssFontFamily = (fontFamily: string): string =>
  fontFamily.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

/** CSS `font-family` value for a single custom family — quoted family + sans-serif fallback. */
export const getFontFamilyCssValue = (fontFamily: string): string => `'${escapeCssFontFamily(fontFamily)}', sans-serif`;

export type GoogleFontsCss2Family = {
  fontFamily: string;
  /** Whether to request the italic face for every specified weight. */
  italic?: boolean;
  /** css2 `wght` axis spec (e.g. '400;700'); omitted → the css2 default (regular 400 only). */
  weights?: string;
};

export type GetGoogleFontsCss2HrefParams = {
  families: GoogleFontsCss2Family[];
  /** Glyph subset: when set, Google serves only these characters' glyphs for EVERY listed family. */
  text?: string;
};

/** Google Fonts css2 stylesheet URL for one or more built-in families, always `display=swap`. */
export const getGoogleFontsCss2Href = ({ families, text }: GetGoogleFontsCss2HrefParams): string => {
  const params = families.map(({ fontFamily, italic, weights }) => {
    if (!weights) {
      return `family=${encodeURIComponent(fontFamily)}${italic ? ':ital@1' : ''}`;
    }

    const axes = italic ? 'ital,wght' : 'wght';
    const values = italic
      ? weights
          .split(';')
          .map((weight) => `1,${weight}`)
          .join(';')
      : weights;

    return `family=${encodeURIComponent(fontFamily)}:${axes}@${encodeURIComponent(values)}`;
  });

  if (text) {
    params.push(`text=${encodeURIComponent(text)}`);
  }

  params.push('display=swap');

  return `${GOOGLE_FONTS_CSS2_URL}?${params.join('&')}`;
};

export type GetFontFaceCssParams = {
  fontFamily: string;
  fontFaceUrl: string;
  /** Backend font variant (for example `regular`, `700`, `italic`, or `700italic`). */
  variant?: string;
};

/** `@font-face` rule registering an uploaded font file under the given family, `font-display: swap`. */
export const getFontFaceCss = ({ fontFamily, fontFaceUrl, variant }: GetFontFaceCssParams): string => {
  const { fontStyle } = parseFontVariant(variant);
  // Uploads register a single file as the family's only face. Without a weight RANGE that face
  // matches weight 400 only, so the title elements' font-semibold/font-bold make the browser
  // synthesize a faux bold (cramped, smeared letters). `100 900` makes the real outlines match
  // every requested weight — and still lets a variable font's wght axis interpolate within it.
  const fontStyleDescriptor = fontStyle === 'italic' ? ' font-style: italic;' : '';

  // %27 keeps a quote in the (already URL-encoded) file url from terminating the CSS string.
  return `@font-face { font-family: '${escapeCssFontFamily(fontFamily)}'; src: url('${fontFaceUrl.replace(/'/g, '%27')}'); font-weight: 100 900;${fontStyleDescriptor} font-display: swap; }`;
};

export type UseFontLoaderOptions = {
  /** Marker attribute stamped on the injected tag; defaults to {@link FONT_LOADER_ATTRIBUTE}. */
  markerAttribute?: string;
};

/**
 * Loads a single font into the document so `font-family` CSS naming it actually renders it:
 * built-in families come from the Google Fonts css2 stylesheet (the backend's curated font list is
 * Google Fonts), uploaded font files are registered directly via `@font-face`. The tag is injected
 * imperatively rather than through `next/head` so the cleanup removes it on unmount, and
 * `next/font` is not applicable — the family/URL are runtime-dynamic.
 *
 * The hook owns exactly one `<head>` tag per mount via effect cleanup, so no id-based dedupe is
 * needed; concurrent mounts of the same font inject duplicate tags, but the browser dedupes the
 * network fetch by URL so duplicates are effectively free. No-op when `font` is undefined.
 */
export const useFontLoader = (font: FontLoaderFont | undefined, options?: UseFontLoaderOptions): void => {
  const fontFamily = font?.fontFamily;
  const variant = font?.variant;
  const fontFaceUrl = font?.fontFaceUrl;
  const markerAttribute = options?.markerAttribute ?? FONT_LOADER_ATTRIBUTE;

  useEffect(() => {
    if (!fontFamily) {
      return;
    }

    let element: HTMLLinkElement | HTMLStyleElement;

    if (fontFaceUrl) {
      const style = document.createElement('style');
      style.textContent = getFontFaceCss({ fontFamily, fontFaceUrl, variant });
      element = style;
    } else {
      const link = document.createElement('link');
      const { fontStyle, fontWeight } = parseFontVariant(variant);
      link.rel = 'stylesheet';
      link.href = getGoogleFontsCss2Href({
        families: [
          {
            fontFamily,
            italic: variant ? fontStyle === 'italic' : false,
            weights: variant ? String(fontWeight) : DEFAULT_GOOGLE_FONT_WEIGHTS,
          },
        ],
      });
      element = link;
    }

    element.setAttribute(markerAttribute, 'true');
    document.head.appendChild(element);

    return () => {
      element.remove();
    };
  }, [fontFamily, variant, fontFaceUrl, markerAttribute]);
};
